<script>
    import GameObject from '@/components/page-main/GameObject.vue';
    import IconArrowExpand from '@/components/icon/IconArrowExpand.vue';
    import { getDisputesSummary, releaseAllProvider } from '@/api/file.js';
    import { filterConflicts, isReadyToRelease } from '@/scripts/providerSearch.mjs';

    export default {
        data() {
            return {
                isCollapse: false,
                isReleasing: false,
            }
        },
        components: {
            GameObject: GameObject,
            IconArrowExpand: IconArrowExpand,
        },
        props: {
            provider: Object,
            // { total, classified }, or null while unknown. Fetched once for the
            // whole list by ProviderList — a request per row buried the backend.
            classification: Object,
        },
        computed: {
            // "7 games", or "4 / 7 games" while a filter hides some.
            gamesLabel() {
                const shown = (this.provider['game_name'] ?? []).length;
                const total = this.provider['games_total'] ?? shown;
                return shown === total ? `${total} games` : `${shown} / ${total} games`;
            },
            // Shown only while the queue still owes this provider a description:
            // "417 / 417" on every row would be noise on a settled catalogue.
            classificationLabel() {
                const c = this.classification;
                if (c == null || c.total === 0 || c.classified >= c.total) return null;
                return `${c.classified.toLocaleString()} / ${c.total.toLocaleString()} described`;
            },
            // Set by the conflicts filter (869fheb1h): 'shown' = only the visible
            // games, 'none' = no button. Unset = the whole provider, as before.
            releaseScope() { return this.provider['release_scope'] ?? 'all' },
            shownGames() { return (this.provider['game_name'] ?? []).filter(isReadyToRelease) },
            releaseLabel() {
                if (this.isReleasing) return 'Releasing…';
                return this.releaseScope === 'shown' ? `Release ${this.shownGames.length} shown` : 'Release All';
            },
            releaseTitle() {
                if (this.releaseScope !== 'shown') return 'Release every unreleased file of this provider';
                const hidden = this.provider['hidden_conflicts'] ?? 0;
                return hidden > 0
                    ? `Only the games on screen. ${hidden} game(s) with conflicts won't be released.`
                    : 'Only the games on screen.';
            },
        },
        methods: {
            // Release every unreleased file of every game under this provider.
            // @click.stop keeps the header's collapse toggle from firing.
            async handleReleaseAll() {
                if (this.isReleasing) return;

                const shown = this.releaseScope === 'shown';
                if (shown && this.shownGames.length === 0) return;
                const confirmed = window.confirm(shown
                    ? `Release ${this.shownGames.length} shown game(s) of "${this.provider['name']}"? Games with conflicts stay unreleased.`
                    : `Release all unreleased files for "${this.provider['name']}"?`
                );
                if (confirmed !== true) return;

                this.isReleasing = true;
                try {
                    // The main page never loads asset types — every other consumer of
                    // this getter sits behind a page that does. Without this the id is
                    // null on a fresh load and the request comes back 422.
                    await this.$store.dispatch('CHECK_ASSET_TYPES');
                    const assetTypeID = this.$store.getters.ASSET_TYPE_IMAGES;
                    if (assetTypeID == null) {
                        throw new Error('image asset type is unknown');
                    }
                    // One request per game: the provider-wide call would take the
                    // hidden conflicting games along. Stops at the first failure.
                    // The page's summary may be minutes old: ask again and skip any
                    // game that picked up a dispute since.
                    let gameIDs = [null];
                    if (shown) {
                        const summary = await getDisputesSummary();
                        if (summary == null) throw new Error('disputes summary failed');
                        const [fresh] = filterConflicts([{ ...this.provider, game_name: this.shownGames }], summary, 'games-clean');
                        if (fresh == null || fresh.release_scope !== 'shown') throw new Error('conflicts appeared');
                        gameIDs = fresh.game_name.map((g) => g.id);
                    }
                    for (const gameID of gameIDs) {
                        const released = await releaseAllProvider(this.provider['id'], assetTypeID, gameID);
                        if (released === false) {
                            throw new Error('release request failed');
                        }
                    }
                } catch (error) {
                    window.alert(
                        `Failed to release files for "${this.provider['name']}". Please try again.`
                    );
                } finally {
                    // Refresh even after a partial failure: some games may be out.
                    await this.$store.dispatch('UPDATE_PROVIDER_AND_GAMES').catch(() => {});
                    this.isReleasing = false;
                }
            }
        }
    }
</script>


<template>
    <div class="right_header">
        <div
            class="provider_toggle cursor_pointer_label"
            @click = "isCollapse = !isCollapse"
        >
            <label>{{ provider['name'] }}</label>
            <span class="games_count">{{ gamesLabel }}</span>
            <IconArrowExpand
                class="header_icon"
                iconWidth = "18px"
                iconHeight = "18px"
                :isActive = "isCollapse"
            />
        </div>
        <span
            v-if="classificationLabel"
            class="classification_progress"
            title="Images described by the AI. The upload does not wait for it — descriptions arrive in the background."
        >{{ classificationLabel }}</span>
        <button
            v-if="releaseScope !== 'none'"
            class="release_all_button"
            :disabled="isReleasing"
            :title="releaseTitle"
            @click="handleReleaseAll"
        >
            {{ releaseLabel }}
        </button>
    </div>
    <ul
        class="clear_ul"
        v-if="isCollapse === false"
    >
        <GameObject
            v-for = "gameValue in provider['game_name']" :key="gameValue['id']"
            :game = "gameValue"
            :providerName = "provider['name']"
            :provideID = "provider['id']"
        />
    </ul>
</template>


<style scoped>
    /* The accordion toggle is only the name + chevron on the left. */
    .provider_toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
    }

    /* Sits before Release All and takes the auto margin, so the button stays
       pinned right whether or not the queue still owes this provider anything. */
    .classification_progress {
        margin-left: auto;
        flex: 0 0 auto;
        font-size: 12px;
        opacity: 0.7;
        white-space: nowrap;
    }

    .classification_progress + .release_all_button {
        margin-left: 12px;
    }

    /* Release All is a separate control pinned to the far right edge, so it no
       longer reads as part of the clickable accordion header. */
    .games_count {
        font-size: 12px;
        padding: 1px 8px;
        border-radius: 10px;
        background: var(--background);
        opacity: 0.85;
        white-space: nowrap;
    }

    .release_all_button {
        margin-left: auto;
        flex: 0 0 auto;
        padding: 4px 12px;
        border: 1px solid currentColor;
        border-radius: 6px;
        background: transparent;
        color: inherit;
        font-size: 13px;
        line-height: 1.2;
    }

    .release_all_button:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.08);
    }

    .release_all_button:disabled {
        opacity: 0.55;
        cursor: default;
    }
</style>
